import { useState } from "react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { updateOpenHouse } from "../../utils/firebasefunctions";
import RecapPreviewModal from "./RecapPreviewModal";
import { useActionDialog } from "../ActionDialog";

export default function RecapPendingBanner({ openHouses, onSent }) {
  const [{ user }] = useStateValue();
  const pending = openHouses.filter((oh) => oh.pendingRecap);
  const [queueIdx, setQueueIdx] = useState(null); // null = closed; number = position in queue
  // Above the early return below: hooks cannot sit behind a condition.
  const { confirm, dialog } = useActionDialog();

  if (pending.length === 0) return null;

  const open = queueIdx !== null;
  const current = open ? pending[queueIdx] : null;
  const total = pending.length;

  const advance = () => {
    if (queueIdx + 1 < pending.length) {
      setQueueIdx(queueIdx + 1);
    } else {
      setQueueIdx(null);
    }
  };

  const exit = () => setQueueIdx(null);

  const noun = pending.length === 1 ? "open house has" : "open houses have";

  return (
    <>
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
        <div className="text-sm text-amber-900">
          <strong>{pending.length} {noun} feedback ready to send</strong>
          <span className="text-amber-700"> · Submitted via the public link by hosting agents. Click below to review and send the seller recaps.</span>
        </div>
        <div className="ml-4 flex items-center gap-2 shrink-0">
          <button
            onClick={() => setQueueIdx(0)}
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-2 rounded-md whitespace-nowrap"
          >
            Send All Recaps ({pending.length})
          </button>
          <button
            onClick={async () => {
              const ok = await confirm({
                title: "Dismiss without sending",
                body: "The sellers will not get a recap and this reminder goes away.",
                note: "The feedback itself is kept.",
                tone: "danger",
                confirmLabel: "Dismiss",
              });
              if (!ok) return;
              // Clear the nag flags; the live openHouses listener hides the
              // banner as soon as the writes land.
              for (const oh of pending) {
                await updateOpenHouse(oh.id, { pendingRecap: false }, user?.id);
              }
              toast.success("Dismissed. No recaps were sent.");
            }}
            className="text-amber-800 hover:text-amber-950 text-sm font-semibold px-2 py-2 whitespace-nowrap"
          >
            Dismiss
          </button>
        </div>
      </div>

      {open && current && (
        <>
          <RecapPreviewModal
            open={true}
            openHouse={current}
            onClose={advance}  // Cancel in batch mode = skip this one
            onSent={(id) => {
              onSent && onSent(id);
              advance();
            }}
          />
          <div className="fixed top-4 right-4 z-[10000] bg-slate-900 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-lg flex items-center gap-3">
            <span>Batch: {queueIdx + 1} of {total}</span>
            <button onClick={exit} className="text-slate-300 hover:text-white">✕ Exit</button>
          </div>
        </>
      )}
      {dialog}
    </>
  );
}
